import React, { useEffect, useRef, useState } from "react";
import { Heart, Mic, Loader2, X, Play, Pause } from "lucide-react";
import api from "../lib/api";
import { AudioPlayer } from "./AudioPlayer";

const MAX_SECONDS = 60;

// Compact recorder for reactions — up to 60 seconds, no waveform, minimal UI.
function ReactionRecorder({ onDone, onCancel }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setUploading(true);
        try {
          const form = new FormData();
          form.append("file", blob, "reaction.webm");
          const res = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
          onDone?.(res.data?.path);
        } catch (e) {
          setError("Couldn't upload. Try again.");
        } finally {
          setUploading(false);
        }
      };
      rec.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next >= MAX_SECONDS) stop();
          return next;
        });
      }, 1000);
    } catch (e) {
      setError("Please allow microphone access.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
  };

  useEffect(() => {
    return () => {
      try { recRef.current?.stop(); } catch (e) { /* ignore */ }
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
      clearInterval(timerRef.current);
    };
  }, []);

  return (
    <div className="p-4 hairline-cream bg-[#FAF7F2]" style={{ borderRadius: 4 }} data-testid="reaction-recorder">
      <div className="flex items-center gap-3">
        {!recording && !uploading && (
          <button onClick={start} className="btn-primary flex items-center gap-2" data-testid="reaction-start-btn">
            <Mic size={14} /> Start recording
          </button>
        )}
        {recording && (
          <>
            <button onClick={stop} className="flex items-center justify-center hairline" style={{ width: 40, height: 40, borderRadius: 999, background: "#B8973A", color: "#FAF7F2" }} data-testid="reaction-stop-btn">
              <span className="block" style={{ width: 12, height: 12, background: "#FAF7F2" }} />
            </button>
            <span className="label-ui text-[11px] text-[#B8973A]" data-testid="reaction-elapsed">
              {elapsed}s / {MAX_SECONDS}s
            </span>
          </>
        )}
        {uploading && (
          <span className="label-ui text-[11px] text-[#8C8278] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Saving your voice…
          </span>
        )}
        <button onClick={onCancel} className="ml-auto label-ui text-[10px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="reaction-cancel-btn">
          Cancel
        </button>
      </div>
      {error && <p className="text-xs text-red-700 mt-2" data-testid="reaction-error">{error}</p>}
      <p className="text-xs text-[#8C8278] italic mt-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
        Up to 60 seconds. A quick word for {`them`}.
      </p>
    </div>
  );
}

export default function StoryReactions({ storyId, storytellerName = "them" }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rRes, meRes] = await Promise.all([
          api.get(`/reactions/${storyId}`),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setItems(rRes.data || []);
        setMe(meRes.data);
      } catch (e) { /* ignore */ }
    })();
  }, [storyId]);

  const sendHeart = async () => {
    setBusy(true);
    try {
      const res = await api.post("/reactions", { story_id: storyId, kind: "heart" });
      setItems((prev) => [res.data, ...prev]);
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  const onVoiceDone = async (audioPath) => {
    setShowRecorder(false);
    if (!audioPath) return;
    setBusy(true);
    try {
      const res = await api.post("/reactions", {
        story_id: storyId,
        kind: "voice",
        audio_path: audioPath,
      });
      setItems((prev) => [res.data, ...prev]);
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  const removeMine = async (reactionId) => {
    try {
      await api.delete(`/reactions/${reactionId}`);
      setItems((prev) => prev.filter((r) => r.reaction_id !== reactionId));
    } catch (e) { /* ignore */ }
  };

  const hearts = items.filter((r) => r.kind === "heart");
  const voices = items.filter((r) => r.kind === "voice");
  const myUserId = me?.user_id;

  return (
    <div className="mt-6 pt-6 border-t hairline-cream" data-testid={`reactions-block-${storyId}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={sendHeart}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 hairline-cream hover:bg-[#F7F0E0] transition-colors disabled:opacity-40"
          style={{ borderRadius: 999 }}
          data-testid={`heart-btn-${storyId}`}
        >
          <Heart size={14} className="text-[#B8973A]" fill={hearts.some((h) => h.user_id === myUserId) ? "#B8973A" : "none"} />
          <span className="text-sm text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif" }}>
            {hearts.length > 0 ? hearts.length : ""} Heart{hearts.length !== 1 && hearts.length > 0 ? "s" : ""}
          </span>
        </button>

        <button
          onClick={() => setShowRecorder(true)}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 hairline-cream hover:bg-[#F7F0E0] transition-colors disabled:opacity-40"
          style={{ borderRadius: 999 }}
          data-testid={`voice-note-btn-${storyId}`}
        >
          <Mic size={14} className="text-[#B8973A]" />
          <span className="text-sm text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif" }}>
            Voice note
          </span>
        </button>

        {hearts.length > 0 && (
          <p className="label-ui text-[10px] text-[#8C8278] ml-auto" data-testid={`hearts-names-${storyId}`}>
            {hearts.slice(0, 3).map((h) => h.from_name || "Someone").join(" · ")}
            {hearts.length > 3 ? ` +${hearts.length - 3}` : ""}
          </p>
        )}
      </div>

      {showRecorder && (
        <div className="mt-4">
          <ReactionRecorder onDone={onVoiceDone} onCancel={() => setShowRecorder(false)} />
        </div>
      )}

      {voices.length > 0 && (
        <div className="mt-5 space-y-3">
          {voices.map((v) => (
            <div key={v.reaction_id} className="p-4 bg-[#FAF7F2] hairline-cream" style={{ borderRadius: 4 }} data-testid={`voice-reaction-${v.reaction_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="label-ui text-[10px] text-[#B8973A]">
                  <Mic size={11} className="inline mr-1" />
                  {v.from_name || "Your family"}
                </p>
                {v.user_id === myUserId && (
                  <button
                    onClick={() => removeMine(v.reaction_id)}
                    className="text-[#8C8278] hover:text-[#111D33] transition-colors"
                    aria-label="Remove"
                    data-testid={`remove-voice-${v.reaction_id}`}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              {v.audio_path && <AudioPlayer path={v.audio_path} label={`A voice note for ${storytellerName}`} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
